// src/pages/ConfirmPage.tsx
import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button } from '../components/ui/Button'

export default function ConfirmPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [status, setStatus] = useState<'verifying'|'expired'|'error'>('verifying')
  const [message, setMessage] = useState('')
  const [resendMessage, setResendMessage] = useState('')

  useEffect(() => {
    const run = async () => {
      // First try to get the session directly
      const { data: { session } } = await supabase.auth.getSession()
      const email = new URLSearchParams(location.search).get('email') || ''
      
      if (session) {
        console.log('🎉 Session found, redirecting to set password')
        navigate(`/set-password?email=${encodeURIComponent(email)}`)
        return
      }

      // Check if we have an error in the URL
      const hash = location.hash.startsWith('#') ? location.hash.slice(1) : location.hash
      const params = new URLSearchParams(hash)
      const errorCode = params.get('error_code')

      if (errorCode === 'otp_expired') {
        console.log('❌ OTP expired error detected')
        setStatus('expired')
        setMessage('Your magic link has expired.')
        return
      }
      
      if (errorCode) {
        setStatus('error')
        setMessage(params.get('error_description') || 'Error confirming your email.')
        return
      }

      // Set up auth state change listener as a fallback
      const { data: authListener } = supabase.auth.onAuthStateChange((event, newSession) => {
        console.log('🔄 Auth state changed:', event)
        if (newSession) {
          authListener.subscription.unsubscribe()
          console.log('🎉 Session created from auth state change, redirecting to set password')
          navigate(`/set-password?email=${encodeURIComponent(email)}`)
        }
      })

      return () => {
        authListener.subscription.unsubscribe()
      }
    }
    
    run()
  }, [location, navigate])

  const handleResend = async () => {
    const email = new URLSearchParams(location.search).get('email') || ''
    
    if (!email) {
      setResendMessage('❌ No email found. Please go back to the signup page.')
      return
    }
    
    console.log('🔄 Resending magic link to:', email)
    const redirectUrl = `${window.location.origin}/confirm?email=${encodeURIComponent(email)}`
    
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { 
        emailRedirectTo: redirectUrl,
      }
    })
    
    setResendMessage(error 
      ? `❌ Error: ${error.message}` 
      : '✅ A new magic link has been sent! Please check your email.'
    )
  }

  if (status === 'expired') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <p className="mb-4 text-center">{message}</p>
        <Button onClick={handleResend} className="w-auto px-6">
          Resend Magic Link
        </Button>
        {resendMessage && <p className="mt-2 text-green-600 text-center">{resendMessage}</p>}
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <p className="text-red-600 text-center">{message}</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <p>Verifying your magic link…</p>
    </div>
  )
}